/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FunnelIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import usePinnedAssetTypes from '../../hooks/usePinnedAssetTypes';
import { sortAssetsByPin } from '../../utils/pinnedAssetsUtils';

/**
 * AssetManagementFiltersBar - A reusable filter component for asset lifecycle management
 * (Assets, Bundles, Deployments, Waves) with accordion interface and pinnable options
 * 
 * @param {Object} props
 * @param {Array} props.filters - Array of filter configurations
 * @param {Function} props.onFilterChange - Callback when any filter changes
 * @param {Function} props.onClearAll - Callback to clear all filters
 * @param {Function} props.onSearch - Callback when Search button is clicked (for manual filtering)
 * @param {Function} props.onFilterDelete - Callback when a filter chip is deleted
 * @param {Object} props.activeFilters - Object containing current filter values
 * @param {boolean} props.showActiveFilters - Whether to show active filter chips
 * @param {string} props.projectCode - Project code for localStorage scope (enables pinning)
 */
const AssetManagementFiltersBar = ({
  filters = [],
  onFilterChange,
  onClearAll,
  onSearch,
  onFilterDelete,
  activeFilters = {},
  showActiveFilters = true,
  title = 'Filters',
  projectCode,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMultiSelectOpen, setIsMultiSelectOpen] = useState({});
  const [searchTerms, setSearchTerms] = useState({});

  // Use pinned asset types hook - always called, but only used if projectCode exists
  const pinnedAssetTypesData = usePinnedAssetTypes(projectCode, 5);
  const pinnedAssetTypes = projectCode ? pinnedAssetTypesData : null;

  const hasActiveFilters = Object.entries(activeFilters).some(([key, value]) => {
    // Skip DESC sort order (default)
    const isDefaultSort = key === 'sortDirection' && value === 'DESC';
    if (isDefaultSort) return false;
    
    // Skip default filters
    const filter = filters.find((f) => f.name === key);
    if (filter?.default === true) return false;
    
    return value !== null && value !== undefined && value !== '' && (!Array.isArray(value) || value.length > 0);
  });

  const renderFilter = (filter) => {
    const {
      type,
      name,
      label,
      placeholder,
      options = [],
      multiple = false,
      hideAllOption = false,
    } = filter;

    const value = activeFilters[name] || (multiple ? [] : '');

    const commonInputClass = "block w-full px-3 py-2.5 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm transition-all duration-200 bg-white hover:border-gray-400";
    const commonLabelClass = "block text-sm font-semibold text-gray-700 mb-2";

    switch (type) {
      case 'text':
      case 'search':
        return (
          <div key={name} className="space-y-1">
            <label htmlFor={`filter-${name}`} className={commonLabelClass}>
              {label}
            </label>
            <div className="relative">
              <input
                id={`filter-${name}`}
                type="text"
                value={value}
                onChange={(e) => onFilterChange(name, e.target.value)}
                placeholder={placeholder}
                className={`${commonInputClass} ${type === 'search' ? 'pr-10' : ''}`}
              />
              {type === 'search' && (
                <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              )}
            </div>
          </div>
        );

      case 'select':
        if (multiple) {
          const selectedCount = Array.isArray(value) ? value.length : 0;
          const isOpen = isMultiSelectOpen[name] || false;
          const searchTerm = searchTerms[name] || '';
          
          // Support for asset type pinning
          const isAssetTypeFilter = name === 'assetTypes' && pinnedAssetTypes;
          const { pinned: pinnedOptions, unpinned: unpinnedOptions } = isAssetTypeFilter 
            ? sortAssetsByPin(options, pinnedAssetTypes.pinnedAssets)
            : { pinned: [], unpinned: options };
          
          const filteredOptions = (isAssetTypeFilter ? [...pinnedOptions, ...unpinnedOptions] : options).filter((option) => {
            const optionLabel = typeof option === 'string' ? option : option.label;
            return optionLabel.toLowerCase().includes(searchTerm.toLowerCase());
          });
          
          return (
            <div key={name} className="space-y-1 relative">
              <label className={commonLabelClass}>
                {label}
              </label>
              <button
                type="button"
                onClick={() => setIsMultiSelectOpen({ ...isMultiSelectOpen, [name]: !isOpen })}
                className={`${commonInputClass} text-left flex items-center justify-between cursor-pointer`}
              >
                <span className={selectedCount > 0 ? 'text-gray-900' : 'text-gray-500'}>
                  {selectedCount > 0 ? `${selectedCount} selected` : 'Select options'}
                </span>
                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
              </button>
              
              <AnimatePresence>
                {isOpen && (
                  <>
                    <motion.div
                      className="fixed inset-0 z-[100] bg-gray-900 bg-opacity-50 backdrop-blur-sm"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onClick={() => {
                        setIsMultiSelectOpen({ ...isMultiSelectOpen, [name]: false });
                        setSearchTerms({ ...searchTerms, [name]: '' });
                      }}
                    />
                    <motion.div
                      className="fixed left-1/2 top-[20%] transform -translate-x-1/2 z-[101] w-[90%] max-w-md bg-white rounded-xl shadow-2xl overflow-hidden"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="px-4 py-3 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="text-sm font-bold text-gray-900">{label}</h3>
                          {isAssetTypeFilter && pinnedAssetTypes.pinnedAssets.length > 0 && (
                            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full font-semibold">
                              {pinnedAssetTypes.pinnedAssets.length} pinned
                            </span>
                          )}
                        </div>
                        <div className="relative">
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerms({ ...searchTerms, [name]: e.target.value })}
                            placeholder="Search..."
                            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                      <div className="max-h-[400px] overflow-y-auto">
                        {filteredOptions.length > 0 ? (
                          filteredOptions.map((option, index) => {
                            const optionValue = typeof option === 'string' ? option : option.value;
                            const optionLabel = typeof option === 'string' ? option : option.label;
                            const isChecked = Array.isArray(value) && value.includes(optionValue);
                            const isPinnedItem = isAssetTypeFilter && pinnedAssetTypes.pinnedAssets.includes(optionValue);
                            const showDivider = isAssetTypeFilter && index === pinnedOptions.filter(opt => {
                              const lbl = typeof opt === 'string' ? opt : opt.label;
                              return lbl.toLowerCase().includes(searchTerm.toLowerCase());
                            }).length;
                            
                            return (
                              <React.Fragment key={optionValue}>
                                {showDivider && searchTerm === '' && pinnedOptions.length > 0 && (
                                  <div className="px-4 py-2 bg-gray-100 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                    Other
                                  </div>
                                )}
                                <div
                                  className={`flex items-center px-4 py-3 hover:bg-indigo-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                                    isPinnedItem ? 'bg-yellow-50' : ''
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => {
                                      const newValue = e.target.checked
                                        ? [...(Array.isArray(value) ? value : []), optionValue]
                                        : (Array.isArray(value) ? value : []).filter(v => v !== optionValue);
                                      onFilterChange(name, newValue);
                                      if (isAssetTypeFilter && e.target.checked) {
                                        pinnedAssetTypes.trackUsage(optionValue);
                                      }
                                    }}
                                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 focus:ring-2 cursor-pointer"
                                  />
                                  <span className="ml-3 flex-1 text-sm text-gray-700">
                                    {optionLabel}
                                  </span>
                                  {isAssetTypeFilter && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        pinnedAssetTypes.togglePin(optionValue);
                                      }}
                                      className={`ml-2 p-1 rounded transition-colors ${
                                        isPinnedItem
                                          ? 'text-yellow-500 hover:text-yellow-600 bg-yellow-100'
                                          : 'text-gray-400 hover:text-gray-600 hover:bg-gray-200'
                                      }`}
                                      title={isPinnedItem ? 'Unpin' : 'Pin to top'}
                                    >
                                      <StarIcon className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </React.Fragment>
                            );
                          })
                        ) : (
                          <div className="px-4 py-8 text-center text-sm text-gray-500">
                            No options found
                          </div>
                        )}
                      </div>
                      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex justify-end">
                        <button
                          type="button"
                          onClick={() => {
                            setIsMultiSelectOpen({ ...isMultiSelectOpen, [name]: false });
                            setSearchTerms({ ...searchTerms, [name]: '' });
                          }}
                          className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
                        >
                          Done
                        </button>
                      </div>
                    </motion.div>
                  </>
                )}
              </AnimatePresence>
            </div>
          );
        }
        
        return (
          <div key={name} className="space-y-1">
            <label htmlFor={`filter-${name}`} className={commonLabelClass}>
              {label}
            </label>
            <div className="relative">
              <select
                id={`filter-${name}`}
                value={value}
                onChange={(e) => onFilterChange(name, e.target.value)}
                className={`${commonInputClass} appearance-none pr-10 cursor-pointer`}
              >
                {!hideAllOption && <option value="">All</option>}
                {options.map((option) => {
                  const optionValue = typeof option === 'string' ? option : option.value;
                  const optionLabel = typeof option === 'string' ? option : option.label;
                  return (
                    <option key={optionValue} value={optionValue}>
                      {optionLabel}
                    </option>
                  );
                })}
              </select>
              <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
            </div>
          </div>
        );

      case 'datetime-local':
        return (
          <div key={name} className="space-y-1">
            <label htmlFor={`filter-${name}`} className={commonLabelClass}>
              {label}
            </label>
            <input
              id={`filter-${name}`}
              type="datetime-local"
              value={value || ''}
              onChange={(e) => onFilterChange(name, e.target.value)}
              className={`${commonInputClass} cursor-pointer`}
            />
          </div>
        );

      default:
        return null;
    }
  };

  const getActiveFilterChips = () => {
    const chips = [];
    
    Object.entries(activeFilters).forEach(([key, value]) => {
      // Skip DESC sort order (default)
      const isDefaultSort = key === 'sortDirection' && value === 'DESC';
      
      if (isDefaultSort) {
        return; // Skip rendering this chip
      }
      
      if (value !== null && value !== undefined && value !== '' && (!Array.isArray(value) || value.length > 0)) {
        const filter = filters.find((f) => f.name === key);
        if (filter) {
          // Skip default filters
          if (filter.default === true) {
            return;
          }
          
          let displayValue = value;
          
          // Handle array values
          if (Array.isArray(value)) {
            displayValue = value.join(', ');
          } 
          // Handle select options - find the label for the value
          else if (filter.type === 'select' && filter.options && !filter.multiple) {
            const matchingOption = filter.options.find(option => {
              const optionValue = typeof option === 'string' ? option : option.value;
              return optionValue === value;
            });
            if (matchingOption) {
              displayValue = typeof matchingOption === 'string' ? matchingOption : matchingOption.label;
            }
          }
          
          // Determine the reset value based on filter type
          const getResetValue = () => {
            // Special case: sortDirection always resets to DESC (default)
            if (key === 'sortDirection') {
              return 'DESC';
            }
            if (filter.multiple) {
              return [];
            }
            if (filter.type === 'select' && filter.options && filter.options.length > 0) {
              // For single select, reset to first option value
              const firstOption = filter.options[0];
              return typeof firstOption === 'string' ? firstOption : firstOption.value;
            }
            return '';
          };
          
          const isDefaultFilter = filter.default === true;
          const chipClassName = isDefaultFilter
            ? "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-gray-400 to-gray-600 text-white shadow-sm"
            : "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-sm";
          
          chips.push(
            <motion.div
              key={key}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              className={chipClassName}
            >
              <span className="font-semibold">{filter.label}:</span>
              <span className="ml-1">{displayValue}</span>
              {!isDefaultFilter && (
                <button
                  type="button"
                  onClick={() => {
                    onFilterChange(key, getResetValue());
                    if (onFilterDelete) {
                      onFilterDelete(key);
                    }
                  }}
                  className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors"
                  title="Remove filter"
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
              )}
            </motion.div>
          );
        }
      }
    });
    
    return chips;
  };

  const accordionVariants = {
    collapsed: { 
      height: 0,
      opacity: 0,
      transition: { 
        height: { duration: 0.3, ease: "easeInOut" },
        opacity: { duration: 0.2 }
      }
    },
    expanded: { 
      height: "auto",
      opacity: 1,
      transition: { 
        height: { duration: 0.3, ease: "easeInOut" },
        opacity: { duration: 0.3, delay: 0.1 }
      }
    }
  };

  return (
    <div className="mb-6">
      <div className="border border-gray-300 rounded-lg shadow-sm bg-white overflow-hidden">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className={`w-full flex items-center justify-between px-5 py-4 text-left transition-all duration-200 ${
            hasActiveFilters
              ? 'bg-gradient-to-r from-indigo-50 to-purple-50 hover:from-indigo-100 hover:to-purple-100'
              : 'bg-white hover:bg-gray-50'
          }`}
        >
          <div className="flex items-center">
            <FunnelIcon className={`w-5 h-5 mr-3 ${hasActiveFilters ? 'text-indigo-600' : 'text-gray-500'}`} />
            <span className={`text-sm font-semibold ${hasActiveFilters ? 'text-indigo-900' : 'text-gray-700'}`}>
              {title}
            </span>
            {hasActiveFilters && (
              <span className="ml-3 inline-flex items-center justify-center px-2.5 py-0.5 text-xs font-bold text-indigo-600 bg-indigo-100 rounded-full">
                {Object.entries(activeFilters).filter(([key, v]) => {
                  const isDefaultSort = key === 'sortDirection' && v === 'DESC';
                  if (isDefaultSort) return false;
                  const filter = filters.find((f) => f.name === key);
                  if (filter?.default === true) return false;
                  return v !== null && v !== undefined && v !== '' && (!Array.isArray(v) || v.length > 0);
                }).length}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {hasActiveFilters && onClearAll && (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  onClearAll();
                }}
                className="text-sm text-indigo-600 hover:text-indigo-800 font-semibold hover:underline transition-colors cursor-pointer"
              >
                Clear All
              </span>
            )}
            {isExpanded ? (
              <ChevronUpIcon className={`w-5 h-5 transition-colors ${hasActiveFilters ? 'text-indigo-600' : 'text-gray-500'}`} />
            ) : (
              <ChevronDownIcon className={`w-5 h-5 transition-colors ${hasActiveFilters ? 'text-indigo-600' : 'text-gray-500'}`} />
            )}
          </div>
        </button>

        {showActiveFilters && hasActiveFilters && (
          <div className="px-5 py-3 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 border-t border-indigo-100">
            <div className="flex flex-wrap gap-2">
              <AnimatePresence>
                {getActiveFilterChips()}
              </AnimatePresence>
            </div>
          </div>
        )}

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              variants={accordionVariants}
              initial="collapsed"
              animate="expanded"
              exit="collapsed"
              className="overflow-hidden"
            >
              <div className="p-6 bg-white border-t border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filters.map((filter) => renderFilter(filter))}
                </div>
                {onSearch && (
                  <div className="mt-6 flex justify-end">
                    <button
                      type="button"
                      onClick={onSearch}
                      className="inline-flex items-center px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 shadow-sm"
                    >
                      <MagnifyingGlassIcon className="w-5 h-5 mr-2" />
                      Search
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

AssetManagementFiltersBar.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['text', 'search', 'select', 'datetime-local']).isRequired,
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      placeholder: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.shape({
            value: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
          }),
        ])
      ),
      multiple: PropTypes.bool,
      hideAllOption: PropTypes.bool,
      default: PropTypes.bool,
      size: PropTypes.oneOf(['small', 'medium', 'large']),
    })
  ).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onClearAll: PropTypes.func,
  onSearch: PropTypes.func,
  onFilterDelete: PropTypes.func,
  activeFilters: PropTypes.object,
  showActiveFilters: PropTypes.bool,
  title: PropTypes.string,
  projectCode: PropTypes.string,
};

export default AssetManagementFiltersBar;
